import React from "react";
import { useMutation, useQueryClient } from "react-query";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  TextInput,
  RichTextInput,
  DateTimeInput,
  CheckboxSwitchField
} from "../../react-hook-form-inputs";
import ButtonOutline from "../../buttons/ButtonOutline";
import { renderRailsErrors } from "../../../utils/rails_error_parser";
import { PortalizedModalContext } from "./../../../components/PortalizedModal";
import {
  useOfferUpdate,
  createOfferUpdate,
  updateOfferUpdate,
} from "../../../queries/offer_updates";
import {
  formatDate,
  parseDateWithoutTimezone
} from "./../../../utils/datetime";

const FormSchema = Yup.object().shape({
  title: Yup.string().required(),
  content: Yup.object()
    .test(
      "has text",
      "Offer update content is required",
      value => value && value.getCurrentContent().getPlainText().trim().length > 0
    ),
  published_at: Yup.date().required(),
  notify_users: Yup.bool().optional()
});

export default function Form({ offerId, updateId, onSuccess }) {
  const queryClient = useQueryClient();
  const confirmModal = React.useContext( PortalizedModalContext );
  const { data:offerUpdate, isLoading } = useOfferUpdate({offer_update_id: updateId})
  const formType = offerUpdate?.id ? "edit" : "create";

  const methods = useForm({
    resolver: yupResolver(FormSchema),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      title: offerUpdate ? offerUpdate.title : "",
      content: RichTextInput.createState(offerUpdate ? offerUpdate.content : ""),
      published_at: offerUpdate ? parseDateWithoutTimezone(offerUpdate.published_at) : "",
      notify_users: true
    }
  });

  React.useEffect(() => {
    if (offerUpdate) {
      methods.setValue("title", offerUpdate.title);
      methods.setValue("content", RichTextInput.createState(offerUpdate.content));
      methods.setValue("published_at", parseDateWithoutTimezone(offerUpdate.published_at));
    }
    else {
      methods.setValue("title", "");
      methods.setValue("content", RichTextInput.createState(null));
      methods.setValue("notify_users", true);
      methods.setValue("published_at", "");
    }
  }, [methods, offerUpdate]);

  const createOfferUpdateMutation = useMutation(
    createOfferUpdate,
    {
      onSuccess: (update) => {
        queryClient.invalidateQueries(
          ["offer-updates-list", offerId],
          {
            exact: true,
          }
        )
        toast.success(`Offer updated submitted for review.`)
        if (typeof onSuccess === "function") onSuccess(update)
      },
      onError: (error) => {
        renderRailsErrors(error)
      },
    }
  )

  const udpateOfferUpdateMutation = useMutation(
    updateOfferUpdate,
    {
      onSuccess: (update) => {
        queryClient.invalidateQueries(
          ["offer-updates-list", offerId],
          {
            exact: true,
          }
        )
        toast.success(`Offer update updated.`)
        if (typeof onSuccess === "function") onSuccess(update)
      },
      onError: (error) => {
        renderRailsErrors(error)
      },
    }
  )

  const onSubmit = data => {
    const attrs = {
      title: data.title,
      content: RichTextInput.contentForSave(data.content),
      published_at: formatDate(data.published_at, 'yyyy-MM-dd'),
      notify_users: data.notify_users
    };

    confirmModal.setModalContent(
      null,
      <div>
        <div className="px-3">
          <p>{`Are you sure you want to ${formType === "edit" ? "save changes to the update?" : "post update for review by Spaced Ventures compliance?"}`}</p>
        </div>
        <div className="p-3 mb-3">
          <button
            className="btn btn-dark btn-inverse rounded-0 px-3 ms-2 float-end"
            onClick={() => {
              if(offerUpdate?.id){
                udpateOfferUpdateMutation.mutate({
                  offer_update_id: offerUpdate.id,
                  values: attrs,
                })
              }
              else {
                createOfferUpdateMutation.mutate({
                  offer_id: offerId,
                  values: attrs,
                })
              }
              confirmModal.closeModal();
            }}
          >
            Yes, {formType === "edit" ? "save changes" : "submit for review"}
          </button>
          <button
            className="btn btn-outline-dark rounded-0 px-3 float-end"
            onClick={ () => {
                confirmModal.closeModal();
                if (typeof onSuccess === "function") onSuccess();
              }
            }
          >Cancel</button>
        </div>
      </div>
    )
    confirmModal.openModal()
  };

  if(offerUpdate?.status === "approved"){
    return (
      <div className="content-centered">
        Can not edit an approved update.
      </div>
    )
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="mt-1">
          { offerUpdate?.status === 'rejected' && offerUpdate?.rejection_reason &&
            (
              <div className="row mt-3">
                <small>Rejection Reason: <span className="text text-danger">{offerUpdate.rejection_reason}</span></small>
              </div>
            )
          }
          <div className="row mt-3">
            <div className="col input-bg-white">
              <TextInput
                name="title"
                placeholder="Title"
              />
            </div>
          </div>
          <div className="row mb-2">
            <div className="col">
              <RichTextInput name="content" hideExternalPlugins={false} containerStyle={{ height: "400px"}}/>
            </div>
          </div>
          <div className="row">
            <div className="col-3 input-bg-white">
              <DateTimeInput name="published_at" placeholder="Published Date" timeFormat={false}/>
            </div>
            { formType === 'create' &&
              <div className="col-6 mb-1 mt-2">
                <div className="col">
                  <CheckboxSwitchField  name="notify_users" placeholder="Notify investors and offer followers" />
                </div>
              </div>
            }
           </div>
          <div className="form-group mt-4">
            <button type="submit"
              className="mx-auto btn btn-outline-dark rounded-0"
              disabled={methods.isSubmitting}
            >
              { formType === "create" ? "Submit for Review" : "Update" }
            </button>
          </div>
        </form>
      </FormProvider>
    </>
  );
}
